import { useState, useEffect } from "react";
import { Plane, Shield, Eye, EyeOff, Check, Trash2, Loader2, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useAuthStore } from "@/lib/auth";
import ClaimQueueCard from "@/components/settings/ClaimQueueCard";
import ForwardingAddressCard from "@/components/settings/ForwardingAddressCard";

interface AirlineInfo {
  code: string;
  name: string;
  loyaltyProgram: string;
  hasAwardSearch: boolean;
  saved: boolean;
  credentials: {
    username: string;
    loyaltyNumber: string;
    loyaltyTier: string;
    isActive: boolean;
    lastVerified: string | null;
    hasPassword: boolean;
  } | null;
}

export default function SettingsPage() {
  const [airlines, setAirlines] = useState<AirlineInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingAirline, setEditingAirline] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const token = useAuthStore((s) => s.token);

  // Form state
  const [formUsername, setFormUsername] = useState("");
  const [formPassword, setFormPassword] = useState("");
  const [formLoyaltyNumber, setFormLoyaltyNumber] = useState("");
  const [formLoyaltyTier, setFormLoyaltyTier] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetchAirlines();
  }, []);

  async function fetchAirlines() {
    try {
      const res = await fetch("/api/settings/airlines", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setAirlines(data.airlines || []);
    } catch (e) {
      console.error("Failed to fetch airlines:", e);
    } finally {
      setLoading(false);
    }
  }

  function startEditing(airline: AirlineInfo) {
    setEditingAirline(airline.code);
    setFormUsername(airline.credentials?.username || "");
    setFormPassword("");
    setFormLoyaltyNumber(airline.credentials?.loyaltyNumber || "");
    setFormLoyaltyTier(airline.credentials?.loyaltyTier || "");
    setShowPassword(false);
    setMessage(null);
  }

  function cancelEditing() {
    setEditingAirline(null);
    setMessage(null);
  }

  async function saveCredentials(code: string) {
    setSaving(true);
    setMessage(null);
    try {
      const body: any = {};
      if (formUsername) body.username = formUsername;
      if (formPassword) body.password = formPassword;
      if (formLoyaltyNumber) body.loyaltyNumber = formLoyaltyNumber;
      if (formLoyaltyTier) body.loyaltyTier = formLoyaltyTier;

      const res = await fetch(`/api/settings/airlines/${code}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to save");
      }

      setMessage({ type: "success", text: "Credentials saved securely" });
      setEditingAirline(null);
      fetchAirlines();
    } catch (e: any) {
      setMessage({ type: "error", text: e.message });
    } finally {
      setSaving(false);
    }
  }

  async function deleteCredentials(code: string) {
    if (!confirm("Remove saved credentials for this airline?")) return;
    try {
      await fetch(`/api/settings/airlines/${code}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessage({ type: "success", text: "Credentials removed" });
      fetchAirlines();
    } catch (e: any) {
      setMessage({ type: "error", text: e.message });
    }
  }

  const tierOptions = ["", "Basic", "Gold", "Platinum", "Platinum Pro", "Executive Platinum", "ConciergeKey"];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Settings className="h-6 w-6" /> Settings
        </h1>
        <p className="text-muted-foreground mt-1">
          Manage your airline accounts and preferences
        </p>
      </div>

      {message && (
        <div className={`p-3 rounded-lg text-sm ${
          message.type === "success" 
            ? "bg-emerald-50 text-emerald-700 border border-emerald-200" 
            : "bg-red-50 text-red-700 border border-red-200"
        }`}>
          {message.text}
        </div>
      )}

      {/* Above the forwarding card on purpose: this one is asking the traveller
          a question, and it renders nothing at all when the queue is empty. */}
      <ClaimQueueCard />

      <ForwardingAddressCard />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plane className="h-5 w-5" /> Airline Accounts
          </CardTitle>
          <CardDescription>
            Link your airline accounts to unlock award/miles pricing and personalized recommendations.
            Credentials are encrypted at rest and never shared.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {airlines.map((airline) => (
            <div
              key={airline.code}
              className="border border-border rounded-lg p-4"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-accent-foreground font-bold text-sm">
                    {airline.code}
                  </div>
                  <div>
                    <div className="font-medium">{airline.name}</div>
                    <div className="text-sm text-muted-foreground">
                      {airline.loyaltyProgram}
                      {airline.hasAwardSearch && (
                        <span className="ml-2 text-xs bg-primary/20 text-accent-foreground px-2 py-0.5 rounded-full">
                          Award Search
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {airline.saved && (
                    <div className="flex items-center gap-1 text-emerald-600 text-sm">
                      <Shield className="h-4 w-4" />
                      <span className="hidden sm:inline">Linked</span>
                    </div>
                  )}
                  {airline.saved && editingAirline !== airline.code && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => deleteCredentials(airline.code)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                  {editingAirline !== airline.code && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => startEditing(airline)}
                    >
                      {airline.saved ? "Edit" : "Link Account"}
                    </Button>
                  )}
                </div>
              </div>

              {/* Saved credential summary */}
              {airline.saved && airline.credentials && editingAirline !== airline.code && (
                <div className="mt-3 pt-3 border-t border-border text-sm text-muted-foreground grid grid-cols-2 gap-2">
                  {airline.credentials.username && (
                    <div>
                      <span className="text-xs text-muted-foreground/60">Username:</span>{" "}
                      {airline.credentials.username}
                    </div>
                  )}
                  {airline.credentials.loyaltyNumber && (
                    <div>
                      <span className="text-xs text-muted-foreground/60">{airline.loyaltyProgram} #:</span>{" "}
                      {airline.credentials.loyaltyNumber}
                    </div>
                  )}
                  {airline.credentials.loyaltyTier && (
                    <div>
                      <span className="text-xs text-muted-foreground/60">Tier:</span>{" "}
                      {airline.credentials.loyaltyTier}
                    </div>
                  )}
                  {airline.credentials.hasPassword && (
                    <div className="text-emerald-600/80">
                      <Shield className="h-3 w-3 inline mr-1" />
                      Password saved (encrypted)
                    </div>
                  )}
                </div>
              )}

              {/* Edit form */}
              {editingAirline === airline.code && (
                <div className="mt-4 pt-4 border-t border-border space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor={`username-${airline.code}`}>
                        Username / Email
                      </Label>
                      <Input
                        id={`username-${airline.code}`}
                        value={formUsername}
                        onChange={(e) => setFormUsername(e.target.value)}
                        placeholder={`Your ${airline.name} login`}
                      />
                    </div>
                    <div>
                      <Label htmlFor={`password-${airline.code}`}>
                        Password
                      </Label>
                      <div className="relative">
                        <Input
                          id={`password-${airline.code}`}
                          type={showPassword ? "text" : "password"}
                          value={formPassword}
                          onChange={(e) => setFormPassword(e.target.value)}
                          placeholder={airline.saved ? "••••••• (unchanged)" : "Your password"}
                        />
                        <button
                          type="button"
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor={`loyalty-${airline.code}`}>
                        {airline.loyaltyProgram} Number
                      </Label>
                      <Input
                        id={`loyalty-${airline.code}`}
                        value={formLoyaltyNumber}
                        onChange={(e) => setFormLoyaltyNumber(e.target.value)}
                        placeholder="e.g., 1234567890"
                      />
                    </div>
                    <div>
                      <Label htmlFor={`tier-${airline.code}`}>
                        Loyalty Tier
                      </Label>
                      <select
                        id={`tier-${airline.code}`}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        value={formLoyaltyTier}
                        onChange={(e) => setFormLoyaltyTier(e.target.value)}
                      >
                        {tierOptions.map((t) => (
                          <option key={t} value={t}>
                            {t || "Select tier..."}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-2">
                    <Button
                      size="sm"
                      onClick={() => saveCredentials(airline.code)}
                      disabled={saving || (!formUsername && !formLoyaltyNumber)}
                    >
                      {saving ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-1" />
                      ) : (
                        <Check className="h-4 w-4 mr-1" />
                      )}
                      Save
                    </Button>
                    <Button variant="ghost" size="sm" onClick={cancelEditing}>
                      Cancel
                    </Button>
                  </div>

                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Shield className="h-3 w-3" />
                    Credentials are encrypted with AES-256-GCM and stored securely.
                    {airline.hasAwardSearch && " Used to check award/miles pricing for your routes."}
                  </p>
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
